import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  Box,
  Typography,
  Paper,
  Button,
  InputAdornment,
  OutlinedInput,
  FormControl,
  FormHelperText,
  CircularProgress,
} from "@mui/material";

import { useCustomerInfoContext } from "../../context/CustomerInfoContext";
import { useNoticeContext } from "../../context/NoticeContext";
import CustomAxios from "../../utils/CustomAxios";

export const ExchangeModal = ({
  isModalOpen,
  setIsModalOpen,
  exchangeType,
  paymentMethod,
  currentBuyRate,
  currentSellRate,
}: {
  isModalOpen: boolean;
  setIsModalOpen: (args: boolean) => void;
  exchangeType: string;
  paymentMethod: string;
  currentBuyRate: number;
  currentSellRate: number;
}) => {
  const { balanceData, customerInfo, getCustomerInfo } =
    useCustomerInfoContext();
  const { setNotice, setSeverity } = useNoticeContext();
  const navigate = useNavigate();
  const [inputAmount, setInputAmount] = useState("");
  const [isInputError, setIsInputError] = useState(false);
  const [isConfirm, setIsConfirm] = useState<boolean>(false);
  const [isSendLoading, setIsSendLoading] = useState<boolean>(false);
  const [isSendError, setIsSendError] = useState<boolean>(false);

  const exchangeText = exchangeType === "sell" ? "換金" : "購入";
  // t単位のレートをg単位に直す
  const exchangeRate =
    (exchangeType === "sell" ? currentSellRate : currentBuyRate) *
    0.001 *
    0.001;
  const exchangeYenText = Math.floor(
    Number(inputAmount) *
      ((exchangeType === "sell" ? currentSellRate : currentBuyRate) *
        0.001 *
        0.001)
  ).toLocaleString();

  const handleInputAmount = (v: string) => {
    if (v.match(/^[0-9]*$/)) {
      setInputAmount(v);
      setIsInputError(Number(v) > customerInfo.jCredit || Number(v) < 10000);
    }
  };

  const onSend = () => {
    setIsSendLoading(true);

    // 交換するJクレジット数。換金の場合は負の値を送信
    const value = Number(inputAmount) * (exchangeType === "sell" ? -1 : 1);

    CustomAxios.post("/customer/jcredit", {
      value,
      paymentMethod,
    })
      .then(() => {
        getCustomerInfo();
        setNotice(`Jクレジットを${exchangeText}しました。`);
        setSeverity("success");
        navigate("/");
      })
      .catch(() => {
        setIsSendError(true);
      })
      .finally(() => {
        setIsSendLoading(false);
      });
  };

  return (
    <Modal open={isModalOpen}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          width: "90%",
          p: 1,
        }}
      >
        {!isConfirm ? (
          <Paper elevation={0} sx={{ mx: 1 }}>
            <Typography color="primary" variant="h6" component="h2">
              {exchangeText}したいJクレジット数を入力
            </Typography>
            <Typography sx={{ ml: 2, mt: 3, mb: 1 }}>保有状況</Typography>
            <Typography sx={{ ml: 4 }}>
              EcoPay残高:
              <span
                style={{
                  marginRight: "10px",
                  marginLeft: "10px",
                  fontWeight: "bold",
                  fontSize: "20px",
                }}
              >
                {balanceData.toLocaleString()}
              </span>
              円
            </Typography>
            <Typography sx={{ ml: 4 }}>
              Jクレジット:
              <span
                style={{
                  marginRight: "10px",
                  marginLeft: "10px",
                  fontWeight: "bold",
                  fontSize: "20px",
                }}
              >
                {customerInfo.jCredit
                  ? customerInfo.jCredit.toLocaleString()
                  : 0}
              </span>
              g-CO2
            </Typography>
            <FormControl sx={{ mx: "10px", my: "30px" }}>
              <Typography sx={{ mb: 1 }}>
                ※最低交換数は10,000g-CO2からです
              </Typography>
              <OutlinedInput
                id="outlined-adornment-amount"
                endAdornment={
                  <InputAdornment
                    position="start"
                    sx={{ fontSize: 34, fontWeight: "bold" }}
                  >
                    g-CO2
                  </InputAdornment>
                }
                autoFocus={true}
                type="number"
                required={true}
                value={inputAmount}
                onChange={(e) => handleInputAmount(e.target.value)}
                sx={{ fontSize: 34, fontWeight: "bold" }}
                inputProps={{ min: "0" }}
                error={isInputError}
              />
              {isInputError && (
                <FormHelperText error sx={{ fontSize: "14px" }}>
                  {Number(inputAmount) > customerInfo.jCredit
                    ? "Jクレジット数が不足しています"
                    : "最低交換数は10,000g-CO2からです"}
                </FormHelperText>
              )}
              <Typography sx={{ ml: 2, fontSize: "20px" }}>
                = {exchangeYenText} 円
              </Typography>
            </FormControl>
            <Paper
              sx={{
                bottom: 0,
                left: 0,
                right: 0,
                m: 2,
                pb: 2,
              }}
              elevation={0}
            >
              <Button
                color="primary"
                variant="contained"
                size="large"
                fullWidth
                onClick={() => {
                  setIsConfirm(true);
                }}
                sx={{ mb: 2 }}
                disabled={
                  isInputError || inputAmount === "" || inputAmount === "0"
                }
              >
                確認
              </Button>
              <Button
                color="primary"
                variant="outlined"
                size="large"
                fullWidth
                onClick={() => setIsModalOpen(false)}
              >
                閉じる
              </Button>
            </Paper>
          </Paper>
        ) : (
          <Paper elevation={0} sx={{ mx: 1 }}>
            <Typography color="primary" variant="h6" component="h2">
              確認
            </Typography>
            <Typography sx={{ mt: 3, textAlign: "center" }}>
              {exchangeType === "sell"
                ? "換金するJクレジット"
                : "消費するEcoPay残高"}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                mt: 1,
                textAlign: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                {exchangeType === "sell"
                  ? Number(inputAmount).toLocaleString()
                  : exchangeYenText}
              </Typography>
              <Typography fontSize="14px" sx={{ ml: 1 }}>
                {exchangeType === "sell" ? "g-CO2" : "円"}
              </Typography>
            </Box>
            <Typography sx={{ mt: 5, textAlign: "center" }}>
              {exchangeType === "sell"
                ? "獲得するEcoPay残高"
                : "購入するJクレジット"}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                mt: 1,
                mb: 5,
                textAlign: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="h4">
                {exchangeType === "sell"
                  ? exchangeYenText
                  : Number(inputAmount).toLocaleString()}
              </Typography>
              <Typography fontSize="14px" sx={{ ml: 1 }}>
                {exchangeType === "sell" ? "円" : "g-CO2"}
              </Typography>
            </Box>
            {isSendError && (
              <Typography sx={{ textAlign: "center", mt: 2, color: "red" }}>
                Jクレジットの{exchangeText}に失敗しました。
              </Typography>
            )}
            {isSendLoading && (
              <Box sx={{ textAlign: "center", m: 2 }}>
                <CircularProgress />
              </Box>
            )}
            {!isSendLoading && (
              <Paper
                sx={{
                  bottom: 0,
                  left: 0,
                  right: 0,
                  m: 2,
                  pb: 2,
                }}
                elevation={0}
              >
                <Button
                  color="primary"
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={onSend}
                  sx={{ mb: 2 }}
                  disabled={
                    isInputError || inputAmount === "" || inputAmount === "0"
                  }
                >
                  {exchangeText}する
                </Button>
                <Button
                  color="primary"
                  variant="outlined"
                  size="large"
                  fullWidth
                  onClick={() => setIsConfirm(false)}
                >
                  戻る
                </Button>
              </Paper>
            )}
          </Paper>
        )}
      </Box>
    </Modal>
  );
};
